<template>
  <div>
    <v-row no-gutters>
      <v-col xs="0" sm="0" md="0" lg="1" xl="1"/>
      <v-col xs="12" sm="12" md="12" lg="10" xl="10" class="pt-8 d-flex flex-column">
        <!-- HEADER SECTION -->
        <v-row no-gutters class="ma-2 pa-2">
          <v-col cols="12">
            <v-card class="d-flex ma-2 pa-2 justify-space-between rounded-xl">

              <!-- CURRENCY BUTTONS -->
              <v-col cols="12" md="5" class="d-flex align-center" style="justify-content: space-evenly;">
                <v-btn
                  class="mr-2"
                  color="primary"
                  @click="selectedCurrency = 'USD/BRL'"
                >
                  USD/BRL
                </v-btn>
                <v-btn
                  class="mr-2"
                  color="primary"
                  @click="selectedCurrency = 'EUR/BRL'"
                  disabled
                >
                  EUR/BRL
                </v-btn>
                <v-btn
                  class="mr-2"
                  color="primary"
                  @click="selectedCurrency = 'GBP/BRL'"
                  disabled
                >
                  GBP/BRL
                </v-btn>
              </v-col>
        
              <!-- PTAX -->
              <v-col
                cols="12" md="2"
                class="d-flex flex-column rounded-xl text-center"
                style="background-color: #2a2a2a;"
              >
                <div class="mb-2" style="font-size: 12px;">PTAX</div>
                <div class="font-weight-bold text--black" style="font-size: 24px;">
                  {{ ptax }}
                </div>
              </v-col>
        
              <!-- DATE SELECTION AND GET QUOTE BUTTON -->
              <v-col cols="12" md="5" class="d-flex align-center" style="justify-content: space-evenly;">
                <div class="d-flex flex-column" style="cursor: default;">
                  <span> {{ $t('pricer.fixingDate') }} </span>
                  <v-menu
                    v-model="fixingDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    disabled
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="fixingDate"
                        prepend-icon="mdi-calendar"
                        readonly
                        dense
                        v-bind="attrs"
                        v-on="on"
                        style="width: 170px"
                        hide-details
                      >
                      </v-text-field>
                    </template>
        
                    <v-date-picker
                      v-model="fixingDate"
                      no-title
                      color="primary"
                      @input="fixingDateMenu = false"
                    >
                    </v-date-picker>
                  </v-menu>
                </div>
                <div class="d-flex flex-column">
                  <span>{{ $t('pricer.maturityDate') }}</span>
                  <v-menu
                    v-model="maturityDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="maturityDate"
                        prepend-icon="mdi-calendar"
                        readonly
                        dense
                        v-bind="attrs"
                        v-on="on"
                        style="width: 170px;"
                        hide-details
                      >
                      </v-text-field>
                    </template>
        
                    <v-date-picker
                      v-model="maturityDate"
                      no-title
                      :min="fixingDate"
                      @input="maturityDateMenu = false"
                      color="primary"
                    >
                    </v-date-picker>
                  </v-menu>
                </div>
                <v-btn
                  class="font-weight-bold"
                  color="primary"
                  @click=prepareQuotePayload()
                >
                  {{ $t('pricer.getQuote') }}
                </v-btn>
              </v-col>
            </v-card>
          </v-col>
        </v-row>

        <!-- Interest Rates Panel -->
        <v-row no-gutters class="ma-2 pa-2">
          <v-col cols="12">
            <v-card class="d-flex flex-column ma-2 pa-2 rounded-xl">
              <h2>{{ $t('pricer.interestRates') }}</h2>
              <v-simple-table class="mt-2">
                <thead>
                  <tr>
                    <th
                      v-for="header in interestRateHeaders"
                      :key="header.value"
                      class="white--text"
                      style="background-color: #2a2a2a"
                    >
                      {{ header.text }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="([rate, item]) in Object.entries(interestRateItems).slice(0, 2)"
                    :key="rate"
                  >
                    <td
                      v-for="header in interestRateHeaders"
                      :key="header.value"
                    >
                      {{ header.value === 'rate' ? rate : item[header.value] }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </v-col>
        </v-row>

        <!-- Forward Rates Section -->
        <v-row no-gutters class="d-flex flex-column ma-2 pa-2">
          <v-row no-gutters class="d-flex align-center justify-space-between pa-2">
            <h2>{{ $t('pricer.forwardRates') }}</h2>
            <div class="text-caption grey--text">
              {{ $t('pricer.lastUpdated') + lastUpdated }}
            </div>
          </v-row>

          <v-row no-gutters>
            <!-- SELL CARD -->
            <v-col cols="6">
              <v-card
                class="ma-2 pa-6 rounded-xl"
                style="background-color: #FF6D00; color: black;"
              >
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="text-h6 font-weight-bold"> {{ $t('quoteInfo.sell') }} USD</div>
                  <v-chip
                    small
                    class="font-weight-bold"
                    color="orange"
                    text-color="black"
                  >
                    {{ $t('quoteInfo.sell').toUpperCase() }}
                  </v-chip>
                </div>

                <div class="text-center text-h4 font-weight-black my-4">
                  {{ forwardSell.rate }}
                </div>

                <div class="d-flex justify-space-between">
                  <span>{{ $t('pricer.spot') }}</span>
                  <span class="font-weight-bold">{{ forwardSell.spot }}</span>
                </div>
                <v-divider class="my-2"></v-divider>
                <div class="d-flex justify-space-between">
                  <span>{{ $t('pricer.points') }}</span>
                  <span class="font-weight-bold">{{ forwardSell.points }}</span>
                </div>
              </v-card>
            </v-col>

            <!-- BUY CARD -->
            <v-col cols="6">
              <v-card
                class="ma-2 pa-6 rounded-xl"
                style="background-color: #FFC107; color: black;"
              >
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="text-h6 font-weight-bold">{{ $t('quoteInfo.buy') }} USD</div>
                  <v-chip
                    small
                    class="font-weight-bold"
                    color="yellow"
                    text-color="black"
                  >
                    {{ $t('quoteInfo.buy').toUpperCase() }}
                  </v-chip>
                </div>

                <div class="text-center text-h4 font-weight-black my-4">
                  {{ forwardBuy.rate }}
                </div>

                <div class="d-flex justify-space-between">
                  <span>{{ $t('pricer.spot') }}</span>
                  <span class="font-weight-bold">{{ forwardBuy.spot }}</span>
                </div>
                <v-divider class="my-2"></v-divider>
                <div class="d-flex justify-space-between">
                  <span>{{ $t('pricer.points') }}</span>
                  <span class="font-weight-bold">{{ forwardBuy.points }}</span>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-row>
      </v-col>
      <v-col xs="0" sm="0" md="0" lg="1" xl="1"/>
    </v-row>
  </div>
</template>

<script>
import { mapActions } from "pinia"
import { useTableStore } from "../../store/curves"

export default {
  data () {
    return {
      selectedCurrency: 'USD/BRL',
      ptax: 5.2450,
      preData: [],
      usdOnshore: [],
      fixingDate: (() => {
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        return new Date(tomorrow.getTime() - (tomorrow.getTimezoneOffset() * 60000)).toISOString().substring(0, 10)
      })(),
      maturityDate: (() => {
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        return new Date(tomorrow.getTime() - (tomorrow.getTimezoneOffset() * 60000)).toISOString().substring(0, 10)
      })(),
      // maturityDate: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10),
      fixingDateMenu: false,
      maturityDateMenu: false,
      interestRateHeaders: [
        { text: this.$t('pricer.headers.rate').toUpperCase(), value: 'rate' },
        { text: this.$t('pricer.headers.bid').toUpperCase(), value: 'bid'  },
        { text: this.$t('pricer.headers.ask').toUpperCase(), value: 'ask' },
        { text: this.$t('pricer.headers.du'), value: 'du'  },
        { text: this.$t('pricer.headers.dc'), value: 'dc' },
      ],
      interestRateItems: {
        PRE: {
          bid: '',
          ask: '',
          du: '',
          dc: '-'
        },
        'USD COUPON': {
          bid: '',
          ask: '',
          du: '-',
          dc: ''
        }
      },
      lastUpdated: '',
      forwardSell: {
        rate: '-',
        spot: '-',
        points: '-'
      },
      forwardBuy: {
        rate: '-',
        spot: '-',
        points: '-'
      }
    }
  },
  mounted () {
    const pre = localStorage.getItem('preData')
    const usd = localStorage.getItem('usdOnshore')

    if (pre && usd) {
      this.preData = JSON.parse(pre)
      this.usdOnshore = JSON.parse(usd)
      this.updateInterestRatesFromInstruments()
    } else {
        this.fetchFuturesData()
        this.updateInterestRatesFromInstruments()
      }
  },
  watch: {
    maturityDate () {
      this.updateInterestRatesFromInstruments()
    }
  },
  computed: {
    // to set a minimum date for maturities date-picker
    // minMaturityDate () {
    //   const today = new Date()
    //   return today.toISOString().substring(0, 10)
    // }
  },
    methods: {
    ...mapActions(useTableStore, [
      "getFuturesData",
      "requestForwardQuote"
    ]),
    fetchFuturesData () {
      this.getFuturesData(false)
        .then(response => {
          const data = response
          this.preData = data.market_data.PRE
          this.usdOnshore = data.market_data.USD_Onshore

          localStorage.setItem('preData', JSON.stringify(this.preData))
          localStorage.setItem('usdOnshore', JSON.stringify(this.usdOnshore))
        })
        .catch(error => {
          console.log(error)
        })
    },
    calculateBusinessDays (startDate, endDate) {
      let count = 0
      const current = new Date(startDate)
      while (current <= endDate) {
        const day = current.getDay()
        if (day !== 0 && day !== 6) count++
        current.setDate(current.getDate() + 1)
      }
      return count
    },
    calculateCalendarDays (startDate, endDate) {
      const diff = endDate.getTime() - startDate.getTime()
      return Math.ceil(diff / (1000 * 3600 * 24))
    },
    updateInterestRatesFromInstruments () {
      const today = new Date()
      const maturity = new Date(this.maturityDate)
      if (isNaN(maturity)) return

      const du = this.calculateBusinessDays(today, maturity)
      const dc = this.calculateCalendarDays(today, maturity)

      const pre = this.interpolateInstrument(this.preData, maturity)
      const coupon = this.interpolateInstrument(this.usdOnshore, maturity)

      this.interestRateItems = {
        PRE: {
          bid: `${pre.bid.toFixed(4)}%`,
          ask: `${pre.ask.toFixed(4)}%`,
          du,
          dc: '-'
        },
        'USD COUPON': {
          bid: `${coupon.bid.toFixed(4)}%`,
          ask: `${coupon.ask.toFixed(4)}%`,
          du: '-',
          dc
        }
      }
    },
    parseExpirationToDate (expiration) {
      const [monthStr, yearStr] = expiration.split(' ')
      const date = new Date(`${monthStr} 1, ${yearStr}`)
      return new Date(date.getFullYear(), date.getMonth() + 1, 0)
    },
    findSurroundingContracts (array, targetDate) {
      const mapped = array.map(item => ({
        ...item,
        date: this.parseExpirationToDate(item.expiration)
      })).sort((a, b) => a.date - b.date)

      let lower = null
      let upper = null

      for (let i = 0; i < mapped.length; i++) {
        const item = mapped[i]
        if (item.date <= targetDate) lower = item
        if (item.date > targetDate) {
          upper = item
          break
        }
      }

      return { lower, upper }
    },
    interpolateInstrument (array, targetDate) {
      const { lower, upper } = this.findSurroundingContracts(array, targetDate)

      if (!lower && !upper) return { bid: 0, ask: 0 }
      if (!lower || !upper || lower.date.getTime() === upper.date.getTime()) {
        const fallback = lower || upper
        return {
          bid: fallback?.bid || 0,
          ask: fallback?.ask || 0
        }
      }

      const range = upper.date - lower.date
      const dist = targetDate - lower.date
      const factor = dist / range

      return {
        bid: lower.bid + (upper.bid - lower.bid) * factor,
        ask: lower.ask + (upper.ask - lower.ask) * factor
      }
    },
    prepareQuotePayload () {
      // part that gets the payload:
      const payload = {
        currency_pair: this.selectedCurrency,
        ptax: parseFloat(this.ptax),
        fixing_date: this.fixingDate,
        maturity_date: this.maturityDate,
        pre: this.interestRateItems.PRE,
        usd_coupon: this.interestRateItems.USD_COUPON
      }
     // part that deals with the real endpoint:
      this.requestForwardQuote(payload)
        .then(response => {
          const data = response.data
          this.forwardSell.rate = data.forward_sell
          this.forwardSell.spot = data.forward_sell_spot
          this.forwardSell.points = data.forward_sell_points
          this.forwardBuy.rate = data.forward_buy
          this.forwardBuy.spot = data.forward_buy_spot
          this.forwardBuy.points = data.forward_buy_points
          this.lastUpdated = new Date().toLocaleTimeString('en-GB', { hour12: false })
        })
      // part only for testing:
      // new Promise(resolve => {
      //   setTimeout(() => {
      //     resolve({
      //       data: {
      //         forwardSell: '5.2769',
      //         forwardSellSpot: '5.2750',
      //         forwardSellPoints: '-0.0002',
      //         forwardBuy: '5.2148',
      //         forwardBuySpot: '5.2130',
      //         forwardBuyPoints: '-0.0005',
      //       }
      //     })
      //   }, 500)
      // })
      // .then(response => {
      //   const data = response.data
      //   this.forwardSell.rate = data.forwardSell
      //   this.forwardSell.spot = data.forwardSellSpot
      //   this.forwardSell.points = data.forwardSellPoints
      //   this.forwardBuy.rate = data.forwardBuy
      //   this.forwardBuy.spot = data.forwardBuySpot
      //   this.forwardBuy.points = data.forwardBuyPoints

      //   this.lastUpdated = new Date().toLocaleTimeString('en-GB', { hour12: false })
      // })
      .catch ((error) => {
        console.log(error)
      })
    },
  },
}
</script>

<style scoped>

</style>